import { useEffect, useState } from 'react';
import type { OpenItemsResponse } from '../../api/types';
import { getOpenPaymentItems } from '../../api/endpoints/payments';
import { parsePaise } from './allocation';

// The browser's search results are not the partner's whole ledger. Keep this
// unfiltered read separate so searching cannot change the summary remainder.
export function usePartnerRemainder(
  initial: OpenItemsResponse,
  enabled = true,
): { total?: bigint; failed?: boolean } {
  const [result, setResult] = useState<{
    source: OpenItemsResponse;
    total?: bigint;
    failed?: boolean;
  }>();
  const complete = initial.orders.length === initial.totalCount;
  useEffect(() => {
    if (!enabled || complete) return;
    let active = true;
    const load = async () => {
      const orders = new Map<number, bigint>();
      let page = 0;
      let totalCount: number;
      do {
        const items = await getOpenPaymentItems('OUT', initial.counterpartyId, {
          page,
          size: 50,
          q: '',
        });
        if (!active) return;
        totalCount = items.totalCount;
        items.orders.forEach((order) =>
          orders.set(order.shipmentId, parsePaise(order.partnerRemaining)),
        );
        page++;
        if (page * 50 >= totalCount && orders.size < totalCount)
          throw new Error('Incomplete partner ledger');
      } while (orders.size < totalCount);
      if (active)
        setResult({
          source: initial,
          total: [...orders.values()].reduce((sum, value) => sum + value, 0n),
        });
    };
    void load().catch(() => {
      if (active) setResult({ source: initial, failed: true });
    });
    return () => {
      active = false;
    };
  }, [initial, complete, enabled]);
  if (!enabled) return {};
  return complete
    ? { total: initial.orders.reduce((sum, order) => sum + parsePaise(order.partnerRemaining), 0n) }
    : result?.source === initial
      ? result
      : {};
}
